import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import { Button, Loading, MultiSelect } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getPrintableDirectorName } from '@commenda/utils';
import { CorporationType, Currency, StakeholderType } from '@prisma/client';
import { KeyedMutator } from 'swr';
import { IncorporationFormHeading } from '../common';
import CorporateStakeholderModal from '../common/corporateStakeholder/CorporateStakeholderModal';
import { CorporateShareholderEquitySplitDistribution } from '../common/corporateStakeholder/EquitySplitDistribution';
import StakeholderOptionsModal from '../common/corporateStakeholder/StakeholderOptionsModal';
import DirectorModal from '../Director/new/DirectorModal';
import CapTableKS from '../EquityInfo/CapTable';
import CompanyValuationInputKS from '../EquityInfo/CompanyValuationInputKS';
import { useEquityFormState } from '../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../EquityInfo/hooks/useEquityResources';
import { EquityDefaultValues } from './Common';
import DirectorForm from './DirectorForm';
import { validateUKStakeholders } from './Validators';

const EquityInfo = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
  companyId,
  corporationId,
  readonly = false,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  token: string;
  companyId: number;
  corporationId: number;
  readonly: boolean;
}) => {
  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateDirectors,
    mutateCorporations,
    mutateCapTableTransactions,
    stakeholderOptions,
    isLoading,
    isError,
  } = useEquityResources(companyId, corporationId);

  const {
    equityInfo,
    setEquityInfo,
    selectedStakeholders,
    setSelectedStakeholders,
    setCorporateShareholderToEdit,
    corporateStakeholderModalVisibility,
    directorModalVisibility,
    stakeholderIdsWithError,
    setStakeholderIdsWithError,
    setCapTableTransactionIdsToDelete,
    isSubmitting,
    stakeholderTypeModalVisibility,
    onAddCorporateShareholder,
    onSubmit,
    selectAddedShareholder,
    onChangeShareholder,
    individualShareholderToEdit,
    corporateShareholderToEdit,
    capTableData,
    onEditShareholderDetails,
  } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: () => {},
    corporationId,
    config: {
      defaultShareCount: 1,
      defaultSharePrice: 1,
      currency: Currency.GBP,
    },
    onSubmitSuccess: () => {
      mutate();
      mutateCapTableTransactions();
      next?.();
    },
    validateStakeholdersAndGetIdsWithError: validateUKStakeholders,
  });

  if (isLoading || isError) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-10 max-w-[700px]">
      <IncorporationFormHeading
        text="Company Ownership"
        additionalText="Please provide information about this company's members."
      />

      <div>
        <CompanyValuationInputKS
          equityInfo={equityInfo}
          setEquityInfo={setEquityInfo}
          shareCountPlaceholder={EquityDefaultValues.shareCount.toString()}
          sharePricePlaceholder={EquityDefaultValues.price.toString()}
          currency={{
            countryCode: 'en-GB',
            name: 'GBP',
            style: '£',
          }}
          helpTexts={{
            mainHeading: 'Capital contributions',
            bodyText:
              'We suggest you keep the default values as they are standard for corporations in the United Kingdom.',
            connectingText: `share${equityInfo.shareCount === 1 ? '' : 's'} valued at`,
          }}
          disabled={readonly}
        />
      </div>

      <div className=" flex flex-col gap-4">
        {data?.requestData?.corporationType === CorporationType.LLC ? (
          <div>
            <h3 className="text-base">Membership Interest Allocation</h3>
            <span className="text-sm text-neutral-2">
              Total allocation must add up to 100%.
            </span>
          </div>
        ) : (
          <div>
            <h3 className="text-base">Shareholder Equity</h3>
          </div>
        )}
      </div>

      <div className=" flex flex-col gap-4 pb-12 border-b">
        <MultiSelect
          disabled={readonly}
          defaultValue={Object.keys(selectedStakeholders || {})}
          id="shareholder-multiselect"
          placeholder="Select shareholders"
          options={
            stakeholderOptions
              ?.map((stakeholder) => {
                return {
                  label: stakeholder.name,
                  value: stakeholder.id,
                };
              })
              ?.sort((a, b) => a.label.localeCompare(b.label)) || []
          }
          onValueChange={onChangeShareholder}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder:
              data?.requestData?.corporationType === CorporationType.CCorp
                ? 'Add a shareholder'
                : 'Add a member',
            onAddItem: stakeholderTypeModalVisibility.show,
          }}
        />
        <StakeholderOptionsModal
          isShown={stakeholderTypeModalVisibility.visible}
          toggle={stakeholderTypeModalVisibility.hide}
          onShowDirectorModal={directorModalVisibility.show}
          onShowCorporateShareholderModal={() => {
            stakeholderTypeModalVisibility.hide();
            corporateStakeholderModalVisibility.show();
          }}
        />
        <CorporateShareholderEquitySplitDistribution
          selectedStakeholders={selectedStakeholders}
          setSelectedStakeholders={setSelectedStakeholders}
          equityInfo={equityInfo}
          onChangeShareholder={onChangeShareholder}
          onEditShareholderDetails={onEditShareholderDetails}
          stakeholderIdsWithError={Array.from(stakeholderIdsWithError)}
          setNotification={setNotification}
          disabled={readonly}
          setCapTableTransactionIdsToDelete={setCapTableTransactionIdsToDelete}
        />
      </div>

      <div className="flex flex-col pt-2 gap-4">
        {data?.requestData?.corporationType === CorporationType.CCorp ? (
          <h3 className="text-base">Ownership Summary</h3>
        ) : null}
        <CapTableKS data={capTableData} showEsopPool={false} />
      </div>

      <div className="flex gap-4">
        {previous ? (
          <Button variant="secondary" onClick={previous}>
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
        ) : null}
        {next ? (
          <Button onClick={onSubmit} disabled={isSubmitting}>
            <span>Continue</span>
            <ProceedIcon className="ml-2 h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <DirectorModal
        isOpen={directorModalVisibility.visible}
        director={individualShareholderToEdit}
        setOpen={directorModalVisibility.hide}
        companyDataMutate={mutateDirectors}
        DirectorForm={DirectorForm}
        setNotification={setNotification}
        companyId={companyId}
        corporationId={corporationId}
        token={token}
        workRequestData={data}
        modalConfig={{
          heading: 'Add a member',
          postSave: (dir) => {
            stakeholderTypeModalVisibility.hide();
            selectAddedShareholder({
              type: StakeholderType.INDIVIDUAL,
              id: dir.id,
              name: getPrintableDirectorName(dir),
            });
            setCorporateShareholderToEdit(undefined);
            setStakeholderIdsWithError((prev) => {
              const newSet = new Set(prev);
              newSet.delete(`INDIVIDUAL_${dir.id}`);
              return newSet;
            });
          },
        }}
      />
      <CorporateStakeholderModal
        selectedCorporateStakeholderId={corporateShareholderToEdit}
        setSelectedCorporateStakeholderId={setCorporateShareholderToEdit}
        companyId={companyId}
        isShown={corporateStakeholderModalVisibility.visible}
        toggle={corporateStakeholderModalVisibility.hide}
        postSave={({
          corporationId: corporateStakeholderId,
          corporationName,
        }) =>
          onAddCorporateShareholder({
            corporateStakeholderId,
            corporationName,
          })
        }
        mutate={async () => {
          await mutateCorporations();
        }}
        setNotification={setNotification}
        headerTextOverride="Add a corporate member"
        DirectorForm={DirectorForm}
        directors={directorsData?.directors || []}
        mutateDirectors={mutateDirectors}
        corporations={corporations || []}
        workRequestData={data}
      />
    </div>
  );
};

export default EquityInfo;
